
export default function MarketingTools() {
  const seoTools = [
    {
      name: "Google Search Console",
      purpose: "Monitor search performance",
      status: "Active",
      icon: "ri-google-line"
    },
    {
      name: "Google Analytics",
      purpose: "Track website traffic",
      status: "Active", 
      icon: "ri-bar-chart-line"
    },
    {
      name: "Bing Webmaster Tools",
      purpose: "Bing search optimization",
      status: "Active",
      icon: "ri-microsoft-line"
    },
    {
      name: "Schema Markup",
      purpose: "Rich snippets display",
      status: "Implemented",
      icon: "ri-code-line"
    }
  ];

  const socialPlatforms = [
    {
      platform: "Facebook Business",
      followers: "12.5K",
      engagement: "4.2%",
      icon: "ri-facebook-fill",
      color: "text-blue-600"
    },
    {
      platform: "Instagram",
      followers: "8.9K", 
      engagement: "6.1%",
      icon: "ri-instagram-line",
      color: "text-pink-600"
    },
    {
      platform: "LinkedIn",
      followers: "3.2K",
      engagement: "3.8%",
      icon: "ri-linkedin-fill",
      color: "text-blue-700"
    },
    {
      platform: "Twitter",
      followers: "5.7K",
      engagement: "2.9%",
      icon: "ri-twitter-fill",
      color: "text-blue-400"
    }
  ];

  const marketingMetrics = [
    {
      metric: "Global Reach",
      value: "52",
      unit: "Countries",
      growth: "+18%",
      icon: "ri-global-line",
      color: "text-green-400"
    },
    {
      metric: "Monthly Visitors",
      value: "847K",
      unit: "Unique",
      growth: "+32%", 
      icon: "ri-user-line",
      color: "text-blue-400"
    },
    {
      metric: "Search Rankings",
      value: "1,247",
      unit: "Keywords",
      growth: "+45%",
      icon: "ri-search-line", 
      color: "text-purple-400"
    },
    {
      metric: "Conversion Rate",
      value: "12.8%",
      unit: "Average",
      growth: "+8%",
      icon: "ri-exchange-line",
      color: "text-orange-400"
    }
  ];

  const backlinks = [
    {
      domain: "tripadvisor.com",
      authority: 94,
      type: "Travel Review",
      status: "Active"
    },
    {
      domain: "expedia.com",
      authority: 92,
      type: "Travel Partner", 
      status: "Active"
    },
    {
      domain: "lonelyplanet.com",
      authority: 89,
      type: "Travel Guide",
      status: "Active"
    },
    {
      domain: "travelandleisure.com",
      authority: 87,
      type: "Editorial",
      status: "Active"
    },
    {
      domain: "fodors.com",
      authority: 82,
      type: "Travel Resource",
      status: "Active"
    }
  ];

  return (
    <section className="py-20 bg-gray-800">
      <div className="container mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Marketing Analytics & Tools
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Advanced tools and metrics driving our worldwide marketing success.
          </p>
        </div>

        {/* Key Metrics */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
          {marketingMetrics.map((metric, index) => (
            <div key={index} className="bg-gray-900/50 rounded-xl p-6 border border-gray-700 text-center">
              <div className="w-12 h-12 flex items-center justify-center bg-gray-800 rounded-lg mx-auto mb-4">
                <i className={`${metric.icon} text-xl ${metric.color}`}></i>
              </div>
              <div className="text-3xl font-bold text-white mb-1">{metric.value}</div>
              <div className="text-gray-400 text-sm mb-2">{metric.unit}</div>
              <div className="text-green-400 text-sm font-medium">{metric.growth} this month</div>
            </div>
          ))}
        </div>

        <div className="grid lg:grid-cols-2 gap-12 mb-16">
          {/* SEO Tools */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-6">SEO Optimization Tools</h3>
            <div className="space-y-4">
              {seoTools.map((tool, index) => (
                <div key={index} className="bg-gray-900/50 rounded-lg p-4 border border-gray-700 flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-10 h-10 flex items-center justify-center bg-blue-600 rounded-lg mr-4">
                      <i className={`${tool.icon} text-white`}></i>
                    </div>
                    <div>
                      <div className="text-white font-medium">{tool.name}</div>
                      <div className="text-gray-400 text-sm">{tool.purpose}</div>
                    </div>
                  </div>
                  <div className="bg-green-900/30 text-green-400 px-3 py-1 rounded-full text-sm">
                    {tool.status}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Social Media Performance */}
          <div>
            <h3 className="text-2xl font-bold text-white mb-6">Social Media Presence</h3>
            <div className="space-y-4">
              {socialPlatforms.map((platform, index) => (
                <div key={index} className="bg-gray-900/50 rounded-lg p-4 border border-gray-700">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center">
                      <div className="w-10 h-10 flex items-center justify-center bg-gray-800 rounded-lg mr-3">
                        <i className={`${platform.icon} ${platform.color}`}></i>
                      </div>
                      <div className="text-white font-medium">{platform.platform}</div>
                    </div>
                    <div className="text-right">
                      <div className="text-white font-semibold">{platform.followers}</div>
                      <div className="text-gray-400 text-sm">followers</div>
                    </div>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-400 text-sm">Engagement Rate</span>
                    <span className="text-green-400 font-medium">{platform.engagement}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* High Authority Backlinks */}
        <div className="bg-gray-900/30 rounded-2xl p-8 border border-gray-700">
          <h3 className="text-2xl font-bold text-white mb-6">High Authority Backlinks</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-700">
                  <th className="text-left text-gray-300 py-3 px-4">Domain</th>
                  <th className="text-left text-gray-300 py-3 px-4">Authority</th>
                  <th className="text-left text-gray-300 py-3 px-4">Link Type</th>
                  <th className="text-left text-gray-300 py-3 px-4">Status</th>
                </tr>
              </thead>
              <tbody>
                {backlinks.map((link, index) => (
                  <tr key={index} className="border-b border-gray-800">
                    <td className="py-3 px-4 text-white font-medium">{link.domain}</td>
                    <td className="py-3 px-4">
                      <div className="flex items-center">
                        <div className="w-8 h-2 bg-gray-700 rounded-full mr-2">
                          <div 
                            className="h-2 bg-green-500 rounded-full"
                            style={{ width: `${link.authority}%` }}
                          ></div>
                        </div>
                        <span className="text-green-400 text-sm">{link.authority}</span>
                      </div>
                    </td>
                    <td className="py-3 px-4 text-gray-300">{link.type}</td>
                    <td className="py-3 px-4">
                      <span className="bg-green-900/30 text-green-400 px-2 py-1 rounded-full text-xs">
                        {link.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Marketing ROI */}
        <div className="mt-16 text-center">
          <div className="bg-gradient-to-r from-green-600 to-blue-600 rounded-2xl p-8">
            <h3 className="text-3xl font-bold text-white mb-4">Marketing ROI Performance</h3>
            <div className="grid md:grid-cols-3 gap-8 mb-6">
              <div>
                <div className="text-4xl font-bold text-white mb-2">324%</div>
                <div className="text-green-100">Return on Ad Spend</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-white mb-2">$2.4M</div>
                <div className="text-green-100">Annual Revenue Growth</div>
              </div>
              <div>
                <div className="text-4xl font-bold text-white mb-2">847K</div>
                <div className="text-green-100">New Customers Acquired</div>
              </div>
            </div>
            <p className="text-green-100 text-lg">
              Our strategic marketing approach delivers measurable results across all global markets.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
